"use client"

import { useState, useCallback } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Copy, Check, ArrowRightLeft, FileJson, Table, Trash2 } from "lucide-react"
import { toast } from "sonner"

type ConversionDirection = "csv-to-json" | "json-to-csv"
type Delimiter = "," | ";" | "\t" | "|"

const delimiters: { value: Delimiter; label: string }[] = [
  { value: ",", label: "Comma (,)" },
  { value: ";", label: "Semicolon (;)" },
  { value: "\t", label: "Tab" },
  { value: "|", label: "Pipe (|)" },
]

function parseCSV(csv: string, delimiter: Delimiter, hasHeaders: boolean): { headers: string[]; rows: string[][] } {
  const lines = csv.trim().split(/\r?\n/)
  if (lines.length === 0) return { headers: [], rows: [] }

  const parseRow = (row: string): string[] => {
    const result: string[] = []
    let current = ""
    let inQuotes = false

    for (let i = 0; i < row.length; i++) {
      const char = row[i]
      const nextChar = row[i + 1]

      if (inQuotes) {
        if (char === '"' && nextChar === '"') {
          current += '"'
          i++
        } else if (char === '"') {
          inQuotes = false
        } else {
          current += char
        }
      } else {
        if (char === '"') {
          inQuotes = true
        } else if (char === delimiter) {
          result.push(current.trim())
          current = ""
        } else {
          current += char
        }
      }
    }
    result.push(current.trim())
    return result
  }

  const parsedRows = lines.map(parseRow)

  if (hasHeaders) {
    const headers = parsedRows[0] || []
    const rows = parsedRows.slice(1)
    return { headers, rows }
  }

  // Generate column headers
  const maxCols = Math.max(...parsedRows.map((r) => r.length))
  const headers = Array.from({ length: maxCols }, (_, i) => `column${i + 1}`)
  return { headers, rows: parsedRows }
}

function csvToJson(csv: string, delimiter: Delimiter, hasHeaders: boolean): string {
  const { headers, rows } = parseCSV(csv, delimiter, hasHeaders)

  const jsonArray = rows.map((row) => {
    const obj: Record<string, string> = {}
    headers.forEach((header, i) => {
      obj[header] = row[i] ?? ""
    })
    return obj
  })

  return JSON.stringify(jsonArray, null, 2)
}

function jsonToCsv(json: string, delimiter: Delimiter, includeHeaders: boolean): string {
  const data = JSON.parse(json)

  if (!Array.isArray(data)) {
    throw new Error("JSON must be an array of objects")
  }

  if (data.length === 0) return ""

  // Get all unique keys from all objects
  const allKeys = new Set<string>()
  data.forEach((item) => {
    if (typeof item === "object" && item !== null) {
      Object.keys(item).forEach((key) => allKeys.add(key))
    }
  })
  const headers = Array.from(allKeys)

  const escapeField = (field: unknown): string => {
    const str = String(field ?? "")
    if (str.includes(delimiter) || str.includes('"') || str.includes("\n")) {
      return `"${str.replace(/"/g, '""')}"`
    }
    return str
  }

  const rows = data.map((item) => {
    return headers.map((header) => escapeField(item[header])).join(delimiter)
  })

  if (includeHeaders) {
    return [headers.map(escapeField).join(delimiter), ...rows].join("\n")
  }
  return rows.join("\n")
}

const sampleCSV = `name,email,age,city
John Doe,john@example.com,28,New York
Jane Smith,jane@example.com,34,Los Angeles
Bob Johnson,bob@example.com,45,Chicago`

const sampleJSON = `[
  {
    "name": "John Doe",
    "email": "john@example.com",
    "age": 28,
    "city": "New York"
  },
  {
    "name": "Jane Smith",
    "email": "jane@example.com",
    "age": 34,
    "city": "Los Angeles"
  },
  {
    "name": "Bob Johnson",
    "email": "bob@example.com",
    "age": 45,
    "city": "Chicago"
  }
]`

export function CsvJsonConverter() {
  const [direction, setDirection] = useState<ConversionDirection>("csv-to-json")
  const [input, setInput] = useState("")
  const [output, setOutput] = useState("")
  const [delimiter, setDelimiter] = useState<Delimiter>(",")
  const [hasHeaders, setHasHeaders] = useState(true)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const convert = useCallback(() => {
    setError(null)
    if (!input.trim()) {
      setOutput("")
      return
    }

    try {
      if (direction === "csv-to-json") {
        const result = csvToJson(input, delimiter, hasHeaders)
        setOutput(result)
        toast.success("Converted to JSON")
      } else {
        const result = jsonToCsv(input, delimiter, hasHeaders)
        setOutput(result)
        toast.success("Converted to CSV")
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Conversion failed"
      setError(message)
      setOutput("")
      toast.error(message)
    }
  }, [input, direction, delimiter, hasHeaders])

  const toggleDirection = () => {
    setDirection((prev) => (prev === "csv-to-json" ? "json-to-csv" : "csv-to-json"))
    setInput(output)
    setOutput("")
    setError(null)
  }

  const loadSample = () => {
    if (direction === "csv-to-json") {
      setInput(sampleCSV)
    } else {
      setInput(sampleJSON)
    }
    setOutput("")
    setError(null)
  }

  const clear = () => {
    setInput("")
    setOutput("")
    setError(null)
  }

  const copyOutput = async () => {
    if (!output) return
    await navigator.clipboard.writeText(output)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    toast.success("Copied to clipboard")
  }

  const inputLabel = direction === "csv-to-json" ? "CSV Input" : "JSON Input"
  const outputLabel = direction === "csv-to-json" ? "JSON Output" : "CSV Output"
  const inputPlaceholder =
    direction === "csv-to-json"
      ? "Paste your CSV data here..."
      : "Paste your JSON array here..."

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {direction === "csv-to-json" ? (
              <>
                <Table className="h-5 w-5" />
                CSV to JSON
              </>
            ) : (
              <>
                <FileJson className="h-5 w-5" />
                JSON to CSV
              </>
            )}
          </CardTitle>
          <CardDescription>
            {direction === "csv-to-json"
              ? "Convert CSV data to JSON array of objects"
              : "Convert JSON array to CSV format"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Options */}
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label>Delimiter</Label>
              <Select value={delimiter} onValueChange={(v) => setDelimiter(v as Delimiter)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {delimiters.map((d) => (
                    <SelectItem key={d.value} value={d.value}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Switch id="headers" checked={hasHeaders} onCheckedChange={setHasHeaders} />
              <Label htmlFor="headers">
                {direction === "csv-to-json" ? "First row is headers" : "Include headers"}
              </Label>
            </div>

            <div className="flex gap-2 ml-auto">
              <Button variant="outline" size="sm" onClick={loadSample}>
                Load Sample
              </Button>
              <Button variant="outline" size="sm" onClick={clear}>
                <Trash2 className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </div>
          </div>

          {/* Input */}
          <div className="space-y-2">
            <Label htmlFor="input">{inputLabel}</Label>
            <Textarea
              id="input"
              placeholder={inputPlaceholder}
              value={input}
              onChange={(e) => {
                setInput(e.target.value)
                setError(null)
              }}
              className="font-mono text-sm min-h-[200px]"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button onClick={convert} disabled={!input.trim()}>
              Convert
            </Button>
            <Button variant="outline" onClick={toggleDirection}>
              <ArrowRightLeft className="h-4 w-4 mr-2" />
              Switch Direction
            </Button>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              {error}
            </div>
          )}

          {/* Output */}
          {output && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{outputLabel}</Label>
                <Button variant="ghost" size="sm" onClick={copyOutput}>
                  {copied ? (
                    <Check className="h-4 w-4 mr-1 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4 mr-1" />
                  )}
                  Copy
                </Button>
              </div>
              <Textarea
                value={output}
                readOnly
                className="font-mono text-sm min-h-[200px] bg-muted"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tips */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Conversion Tips</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p><strong>CSV to JSON:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Each row becomes a JSON object</li>
            <li>Column headers become object keys</li>
            <li>Quoted fields with commas are handled correctly</li>
            <li>Enable &quot;First row is headers&quot; to use the first row as keys</li>
          </ul>
          <p className="mt-4"><strong>JSON to CSV:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Input must be a JSON array of objects</li>
            <li>Object keys become column headers</li>
            <li>Fields with delimiters are automatically quoted</li>
            <li>Nested objects/arrays are converted to strings</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  )
}
